import { useMemo } from "react";
import { StyleSheet, Text, View } from "@react-pdf/renderer";

import PdfBrandBarLockup, {
  usePdfBrandBar,
} from "../ui/images/PdfBrandBarLockup";
import PdfBuilderLogo from "../ui/images/PdfBuilderLogo";
import PdfColumn from "../components/layout/PdfColumn";
import PdfDivider from "../components/layout/PdfDivider";
import PdfRow from "../components/layout/PdfRow";
import PdfSection from "../components/layout/PdfSection";
import PdfText from "../components/typography/PdfText";
import { pdfBuilderGetTypeValue } from "../utils/pdfBuilderGetTypeValue";
import PdfPage from "./PdfPage";

/**
 * A PDF page whose header is a full-bleed brand bar — the brand-colored band
 * with the logo on it, the way the portal header reads — rather than a title
 * with a small corner mark.
 *
 * PARTNER-AWARE, and deliberately conservative about it. The band + lockup pair
 * lives in PdfBrandBarLockup: Eastview gets its teal band, Lendmarq the blue one
 * from its app header, and any partner whose mark is dark-on-light (Resfin,
 * Resilender, Handsome Homebuyer) gets `usePdfBrandBar() === null` and falls
 * through to the plain title-left / mark-right header below. The bar is opt-in
 * per brand rather than a global restyle for exactly that reason.
 */

// PdfPage's own padding. The bar cancels it with negative margins to reach the
// page edges; keep the two in step.
const PAGE_PADDING = 30;

const styles = StyleSheet.create({
  bar: {
    // backgroundColor comes from usePdfBrandBar() — it is per-partner.
    marginTop: -PAGE_PADDING,
    marginLeft: -PAGE_PADDING,
    marginRight: -PAGE_PADDING,
    marginBottom: 22,
    paddingVertical: 14,
    paddingHorizontal: PAGE_PADDING,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  barTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  barSubtitle: {
    fontSize: 8,
    color: "rgba(255,255,255,0.85)",
    marginTop: 2,
  },
});

const PdfPageBrandBar = ({
  children,
  pageNumber,
  pageTitle,
  pageSubtitle,
  submittedBy,
  date,
  submittedDate,
}) => {
  const brandBar = usePdfBrandBar();

  return (
    <PdfPage padding={PAGE_PADDING}>
      {brandBar ? (
        <View style={[styles.bar, { backgroundColor: brandBar.color }]}>
          <PdfBrandBarLockup width={104} />
          <View style={{ alignItems: "flex-end" }}>
            {pageTitle ? (
              <Text style={styles.barTitle}>{pageTitle}</Text>
            ) : null}
            {pageSubtitle ? (
              <Text style={styles.barSubtitle}>{pageSubtitle}</Text>
            ) : null}
          </View>
        </View>
      ) : (
        // No branded band for this partner: the pre-existing header shape —
        // title left, mark right.
        <PdfRow
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            display: "flex",
            marginBottom: 18,
          }}
        >
          <PdfColumn>
            {pageTitle ? (
              <Text style={{ fontSize: 15, fontWeight: "bold", color: "#333333" }}>
                {pageTitle}
              </Text>
            ) : null}
            {pageSubtitle ? (
              <PdfText variant="small" style={{ marginTop: 2 }}>
                {pageSubtitle}
              </PdfText>
            ) : null}
          </PdfColumn>
          <PdfBuilderLogo size={32} />
        </PdfRow>
      )}

      {children}

      <PdfFooter>
        <PdfRow style={{ justifyContent: "space-between" }}>
          <FootSubmissionDetails
            submittedBy={submittedBy}
            submittedDate={submittedDate}
            date={date}
          />
          <PdfText variant={"small"}>Page {pageNumber}</PdfText>
        </PdfRow>
      </PdfFooter>
    </PdfPage>
  );
};

const PdfFooter = ({ children, style }) => {
  const footerStyles = useMemo(
    () => ({
      position: "absolute",
      bottom: -15,
      left: 0,
      width: "100%",
      padding: 20,
      color: "#666666",
      ...style,
    }),
    [style],
  );
  return (
    <PdfSection style={footerStyles}>
      <PdfDivider />
      {children}
    </PdfSection>
  );
};

const FootSubmissionDetails = ({ submittedBy, submittedDate, date }) => {
  if (!submittedBy && !submittedDate && !date) return null;
  return (
    <PdfColumn>
      {submittedDate && (
        <PdfText variant={"small"} style={{ marginBottom: 2 }}>
          Submitted: {pdfBuilderGetTypeValue(submittedDate, "date")}
        </PdfText>
      )}
      {date && (
        <PdfText variant={"small"} style={{ marginBottom: 2 }}>
          Date: {pdfBuilderGetTypeValue(date, "date")}
        </PdfText>
      )}
      {submittedBy && (
        <PdfText variant={"small"}>Prepared by: {submittedBy}</PdfText>
      )}
    </PdfColumn>
  );
};

export default PdfPageBrandBar;
